import React, { useCallback, useEffect, useMemo, useState } from 'react'

import {
  Box,
  Button,
  CircularProgress,
  Grid,
  ListSubheader,
  Popper,
  TextField,
  styled,
} from '@mui/material'
import Autocomplete, { autocompleteClasses } from '@mui/material/Autocomplete'
import { isEmpty, sortBy } from 'lodash'
import { useSnackbar } from 'notistack'
import { ListChildComponentProps, VariableSizeList } from 'react-window'

import { t } from 'i18n'
import DialogTeleport from './DialogTeleport'
import { initialDialog } from 'constants/dialog'
import { ManageShareReport } from 'pages'

export type DemographicsFiltersType = {
  beheardReport?: any
  demographics: any[]
  demographicsIsLoading: boolean
  filtersState: any
  setFiltersState: any
  hasResetFilters?: boolean
  roles?: any[]
  refetch?: (filters?: any) => any
  allowShareReport?: boolean
}

const LISTBOX_PADDING = 8 // px
const EST_CHARS_PER_LINE = 32
const LINE_HEIGHT = 20
const MIN_OPTION_HEIGHT = 52
const MAX_OPTION_HEIGHT = 120

function renderRow(props: ListChildComponentProps) {
  const { data, index, style } = props
  const dataSet = data[index]
  const inlineStyle = {
    ...style,
    top: (style.top as number) + LISTBOX_PADDING,
  }

  if (Object.prototype.hasOwnProperty.call(dataSet, 'group')) {
    return (
      <ListSubheader key={dataSet.key} component='div' style={inlineStyle}>
        {dataSet.group}
      </ListSubheader>
    )
  }

  return (
    <Box width={1} {...dataSet[0]} style={inlineStyle}>
      {dataSet[1]}
    </Box>
  )
}

const OuterElementContext = React.createContext({})

const OuterElementType = React.forwardRef<HTMLDivElement>((props, ref) => {
  const outerProps = React.useContext(OuterElementContext)
  return <div ref={ref} {...props} {...outerProps} />
})

function useResetCache(data: any) {
  const ref = React.useRef<VariableSizeList>(null)
  useEffect(() => {
    if (ref.current != null) {
      ref.current.resetAfterIndex(0, true)
    }
  }, [data])
  return ref
}

// Adapter for react-window to use inside MUI Autocomplete listbox
const ListboxComponent = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLElement>
>(function ListboxComponent(props, ref) {
  const { children, ...other } = props
  const itemData: React.ReactChild[] = []
  ;(children as React.ReactChild[]).forEach(
    (item: React.ReactChild & { children?: React.ReactChild[] }) => {
      itemData.push(item)
      itemData.push(...(item.children || []))
    },
  )

  const itemCount = itemData.length

  const getChildSize = (child: React.ReactChild) => {
    if (Object.prototype.hasOwnProperty.call(child, 'group')) {
      return 48
    }
    const estimated = (child as any)?.props?.['data-option-height']
    return estimated || MIN_OPTION_HEIGHT
  }

  const getHeight = () => {
    if (itemCount > 8) {
      return 8 * MIN_OPTION_HEIGHT
    }
    return itemData.map(getChildSize).reduce((a, b) => a + b, 0)
  }

  const gridRef = useResetCache(itemCount)

  return (
    <div ref={ref}>
      <OuterElementContext.Provider value={other}>
        <VariableSizeList
          itemData={itemData}
          height={getHeight() + 2 * LISTBOX_PADDING}
          width='100%'
          ref={gridRef}
          outerElementType={OuterElementType as any}
          innerElementType='ul'
          itemSize={index => getChildSize(itemData[index])}
          overscanCount={3}
          itemCount={itemCount}
        >
          {renderRow}
        </VariableSizeList>
      </OuterElementContext.Provider>
    </div>
  )
})

const StyledPopper = styled(Popper)({
  [`& .${autocompleteClasses.listbox}`]: {
    boxSizing: 'border-box',
    '& ul': {
      padding: 0,
      margin: 0,
    },
    '& li': {
      paddingLeft: 0,
      paddingRight: 0,
    },
  },
})

function normalizeOption(option: any) {
  const name = option?.name || ''
  let index = Number.MAX_SAFE_INTEGER

  if (name.includes('Below')) {
    const splittedData = name.split(' ')[1]
    index = parseInt(splittedData, 10) - 1
  } else if (name.includes('-')) {
    const splittedData = name.split('-')[0]
    index = parseInt(splittedData, 10)
  } else if (name.includes('+')) {
    const splittedData = name.split('-')[0]
    index = parseInt(splittedData, 10)
  }

  return {
    ...option,
    index,
  }
}

const estimateHeight = (name: string) => {
  const lines = Math.max(1, Math.ceil((name?.length || 0) / EST_CHARS_PER_LINE))
  const height = 16 + lines * LINE_HEIGHT
  return Math.max(MIN_OPTION_HEIGHT, Math.min(MAX_OPTION_HEIGHT, height))
}

function DemographicsFilters({
  beheardReport,
  demographics,
  demographicsIsLoading,
  filtersState,
  setFiltersState,
  hasResetFilters,
  roles,
  refetch,
  // allowShareReport,
}: DemographicsFiltersType) {
  const [resetFilterMode, setResetFilterMode] = useState(false)
  const [localFilters, setLocalFilters] = useState(filtersState)
  const [dialogShareReport, setDialogShareReport] = useState(initialDialog)

  const { enqueueSnackbar } = useSnackbar()

  const categories = demographics || []

  // Prepare categories and options only when demographics change
  const normalizedCategories = useMemo(
    () =>
      sortBy(categories, category => category.name).map(category => ({
        id: category.id,
        name: category.name,
        options: (category?.demographics_attributes || [])
          .map(normalizeOption)
          .sort((a, b) => a.index - b.index)
          .map(option => ({ ...option, index: undefined })),
      })),
    [categories],
  )

  const optionsIndexByCategory = useMemo(() => {
    const map = new Map()
    normalizedCategories.forEach(category => {
      const indexMap = new Map()
      category.options.forEach(opt => {
        indexMap.set(opt.id, opt)
      })
      map.set(category.id, indexMap)
    })
    return map
  }, [normalizedCategories])

  // Keep local filters in sync when the applied filters change externally
  useEffect(() => {
    setLocalFilters(filtersState)
  }, [filtersState])

  useEffect(() => {
    if (isEmpty(filtersState) && resetFilterMode) {
      refetch && refetch(filtersState)
      setResetFilterMode(false)
      enqueueSnackbar(
        t('components.demographicsFilters.snackbar.success.resetFilters'),
        {
          variant: 'success',
        },
      )
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filtersState, resetFilterMode])

  const resetFilters = () => {
    setLocalFilters({})
    setFiltersState({})
    setResetFilterMode(true)
    // Force a page refresh with a clean URL to avoid re-applying notification filters
    const params = new URLSearchParams(window.location.search)
    Array.from(params.keys()).forEach(key => {
      if (key === 'roles' || key === 'roles[]' || key.startsWith('criteria[')) {
        params.delete(key)
      }
    })
    const newSearch = params.toString()
    const newUrl = `${window.location.pathname}${
      newSearch ? `?${newSearch}` : ''
    }`
    window.history.replaceState(null, '', newUrl)
    window.location.reload()
  }

  const selectedOptionsByCategory = useMemo(() => {
    const selected: Record<string, any[]> = {}
    Object.entries(localFilters || {}).forEach(([categoryId, ids]) => {
      const idx = optionsIndexByCategory.get(Number(categoryId))
      selected[categoryId] = Array.isArray(ids)
        ? ids
            .map(id => idx?.get(id))
            .filter(Boolean)
        : []
    })
    if (localFilters?.roles && Array.isArray(localFilters.roles)) {
      selected['roles'] = roles
        ? roles.filter(role => localFilters.roles.includes(role.id))
        : []
    }
    return selected
  }, [localFilters, optionsIndexByCategory, roles])

  const handleCategoryChange = useCallback(
    (categoryId: number, values: any[]) => {
      setLocalFilters(prev => ({
        ...prev,
        [categoryId]: values.map(value => value.id),
      }))
    },
    [],
  )

  const handleRolesChange = useCallback(values => {
    setLocalFilters(prev => ({
      ...prev,
      roles: values.map(value => value.id),
    }))
  }, [])

  const applyFilters = useCallback(() => {
    setFiltersState(localFilters)
    refetch && refetch(localFilters)
  }, [localFilters, refetch, setFiltersState])

  return isEmpty(demographics) ? (
    <Grid container justifyContent='center' my={5}>
      {t('thereAreNo', {
        name: t('routes.demographics'),
      })}
    </Grid>
  ) : demographicsIsLoading ? (
    <Grid container justifyContent='center'>
      <CircularProgress size={32} />
    </Grid>
  ) : (
    <>
      <Grid container spacing={2}>
        {normalizedCategories.map(category => (
          <Grid item xs={12} sm={4} md={4} lg={3} xl={2} key={category.id}>
            <Autocomplete
              multiple
              disableCloseOnSelect
              ListboxComponent={ListboxComponent as any}
              PopperComponent={StyledPopper as any}
              options={category.options}
              value={selectedOptionsByCategory[category.id] || []}
              onChange={(_, value) => handleCategoryChange(category.id, value)}
              getOptionLabel={option => option.name}
              isOptionEqualToValue={(option, value) => option.id === value.id}
              renderOption={(props, option, { selected }) => [
                {
                  ...props,
                  'data-option-height': estimateHeight(option.name),
                },
                <Box
                  component='li'
                  sx={{
                    display: 'block',
                    whiteSpace: 'normal',
                    wordBreak: 'break-word',
                    lineHeight: 1.25,
                    width: '100%',
                    py: 0.75,
                    px: 0.25,
                    color: selected ? 'primary.main' : 'text.primary',
                  }}
                  {...props}
                >
                  {option.name}
                </Box>,
              ]}
              renderInput={params => (
                <TextField
                  {...params}
                  label={category.name}
                  variant='outlined'
                  inputProps={{
                    ...params.inputProps,
                    'aria-label': `${category.name} filter`,
                  }}
                />
              )}
            />
          </Grid>
        ))}

        {roles && roles?.length > 0 && (
          <Grid item xs={12} sm={4} md={4} lg={3} xl={2} key={'roles'}>
            <Autocomplete
              multiple
              disableCloseOnSelect
              options={roles}
              ListboxComponent={ListboxComponent as any}
              PopperComponent={StyledPopper as any}
              value={selectedOptionsByCategory['roles'] || []}
              onChange={(_, value) => handleRolesChange(value)}
              getOptionLabel={option => option.name}
              isOptionEqualToValue={(option, value) => option.id === value.id}
              renderOption={(props, option, { selected }) => [
                {
                  ...props,
                  'data-option-height': estimateHeight(option.name),
                },
                <Box
                  component='li'
                  sx={{
                    display: 'block',
                    whiteSpace: 'normal',
                    wordBreak: 'break-word',
                    width: '100%',
                    py: 0.75,
                    px: 0.25,
                    color: selected ? 'primary.main' : 'text.primary',
                  }}
                  {...props}
                >
                  {option.name}
                </Box>,
              ]}
              renderInput={params => (
                <TextField
                  {...params}
                  label='Roles'
                  variant='outlined'
                  inputProps={{
                    ...params.inputProps,
                    'aria-label': `Roles filter`,
                  }}
                />
              )}
            />
          </Grid>
        )}
      </Grid>

      {(hasResetFilters || refetch) && (
        <Grid container justifyContent='flex-end' spacing={2}>
          {hasResetFilters && (
            <Grid item>
              <Box mt={3}>
                <Button
                  variant='outlined'
                  color='secondary'
                  onClick={resetFilters}
                >
                  {t('components.demographicsFilters.buttons.resetFilters')}
                </Button>
              </Box>
            </Grid>
          )}
          {refetch && (
            <Grid item>
              <Box mt={3}>
                <Button variant='contained' color='primary' onClick={applyFilters}>
                  {t('components.demographicsFilters.buttons.applyFilters')}
                </Button>
              </Box>
            </Grid>
          )}
        </Grid>
      )}

      <DialogTeleport
        dialogOpen={dialogShareReport.isOpen}
        dialogSize='md'
        dialogTitle={'Share this Report'}
        handleDialogClose={() => setDialogShareReport(initialDialog)}
      >
        <ManageShareReport
          dialog={dialogShareReport}
          closeDialog={() => setDialogShareReport(initialDialog)}
          surveyId={beheardReport?.survey?.id}
          isAddMode
        />
      </DialogTeleport>
    </>
  )
}

export default React.memo(DemographicsFilters)
